import * as THREE from 'three/webgpu';
import { GRADIENT_3 } from '../ToonMaterials.js';
import { chartPartTransform } from '@chaos/sim';

/** Batches retain part-local vertices so future candle edits can move a whole part. */
export function buildChartVisual(root, map, asset) {
  for (const batch of asset.batches) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(batch.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(batch.normals, 3));
    const color = new THREE.Color().fromArray(batch.color);
    const material = batch.emission
      ? new THREE.MeshBasicNodeMaterial({ color: color.clone().multiplyScalar(Math.min(batch.emission, 1.4)) })
      : new THREE.MeshToonNodeMaterial({ color, gradientMap: GRADIENT_3, emissive: color, emissiveIntensity: .08 });
    const mesh = new THREE.Mesh(geometry, material);
    const transform = chartPartTransform(batch, map.visual ?? map.previewScale);
    mesh.position.fromArray(transform.position); mesh.scale.fromArray(transform.scale);
    mesh.name = `Blender Chart / ${batch.name}`;
    mesh.userData.partId = batch.partId; mesh.userData.ownsMaterial = true;
    // Millimeter-separated Chart floor plates/rims cannot resolve cleanly in
    // the shadow map. Receive candle/character shadows, but do not render those
    // cosmetic layers as casters. This renderer also serves other arena drafts.
    const chartFloor = asset.source === 'the-chart.blend'
      && /^chart_floor_(?:\d+_\d+|foundation)$/.test(batch.partId);
    mesh.castShadow = !batch.emission && !chartFloor;
    mesh.receiveShadow = !batch.emission;
    root.add(mesh);
  }
}

/** Keep the playable surface types legible when artwork cannot load. */
export function buildChartFallback(root, map) {
  for (const [kind, color] of [['normal', 0x36786e], ['hazard', 0xff4200], ['heal', 0x37df79], ['conveyor', 0xffd166]]) {
    const platforms = map.platforms.filter(p => p.solid && p.surface.kind === kind);
    if (kind === 'conveyor' && !platforms.length) continue;
    const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicNodeMaterial({ color }), platforms.length);
    const matrix = new THREE.Matrix4();
    platforms.forEach((p, i) => {
      matrix.makeScale(p.hx * 2, p.hy * 2, p.hz * 2); matrix.setPosition(p.x, p.y, p.z); mesh.setMatrixAt(i, matrix);
    });
    mesh.name = `Chart ${kind} collision fallback`;
    mesh.userData.surfaceKind = kind; mesh.userData.ownsMaterial = true;
    root.add(mesh);
  }
}
